import { ERROR_MESSAGES } from "../../shared/errors";
import { EnvxSchema, EnvxResult, EnvxType, EnvxError, ParsedLine } from "../types";
import { isEmail, toBoolean } from "../utils";

/**
 * Parses a single environment variable value according to its declared type.
 *
 * @param key - The environment variable name.
 * @param value - The raw string value to parse.
 * @param type - Expected type of the value.
 * @param values - Allowed enum values if type is "enum".
 * @throws {EnvxError} When the value doesn't conform to the expected type.
 */
function parseValueByType(
  key: string,
  value: string,
  type: EnvxType,
  values?: string[]
): string | number | boolean {
  switch (type) {
    case "string":
      return value;

    case "number": {
      const num = Number(value);
      if (isNaN(num)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.validator.invalidNumber(key, value)
        );
      }
      return num;
    }

    case "boolean": {
      return toBoolean(value, key, true);
    }

    case "enum": {
      if (!values?.length) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.validator.missingEnumValues(key)
        );
      }
      if (!values.includes(value)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.validator.invalidEnum(key, value, values)
        );
      }
      return value;
    }

    case "email": {
      if (!isEmail(value)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.validator.invalidEmail(key, value)
        );
      }
      return value;
    }

    case "url": {
      try {
        new URL(value);
        return value;
      } catch {
        throw new EnvxError(
          ERROR_MESSAGES.lib.validator.invalidUrl(key, value)
        );
      }
    }

    default:
      throw new EnvxError(
        ERROR_MESSAGES.lib.validator.unsupportedType(key, type)
      );
  }
}

/**
 * Validates and parses environment variables based on the provided schema.
 * @throws {EnvxError} When required variables are missing or values are invalid.
 */
export function validateEnv(
  parsed: ParsedLine[],
  schema?: EnvxSchema
): EnvxResult {
  const result: EnvxResult = {};
  const parsedMap = new Map(parsed.map(({ key, value }) => [key, value]));

  for (const key in schema) {
    const { type, required, default: defValue, values } = schema[key];
    const rawValue = parsedMap.get(key);

    if (rawValue !== undefined) {
      // >_if value have check type
      result[key] = parseValueByType(
        key,
        rawValue,
        type,
        type === "enum" ? values : undefined
      );
      continue;
    }

    if (defValue !== undefined) {
      result[key] = parseValueByType(
        key,
        String(defValue),
        type,
        type === "enum" ? values : undefined
      );
      continue;
    }

    if (required) {
      throw new EnvxError(ERROR_MESSAGES.lib.validator.requiredMissing(key));
    }
  }

  for (const [key, value] of parsedMap.entries()) {
    if (!(key in result)) {
      result[key] = value;
    }
  }

  return result;
}
